import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { v4 as uuidv4 } from 'uuid'

/**
 * State for the inline Cash login flow on the Next-Gen Login Domain APIs (CASHDAP-3104).
 *
 * The flow carries a `loginIntentToken` plus a container of signed `attestations`
 * (the two-token model). Each verified factor appends an attestation; the container
 * is exchanged for a session (`accessToken`) at RedeemLoginIntent.
 */
export interface CashAuthState {
  /** login_intent_token from CreateLoginIntent, used for every subsequent call. */
  loginIntentToken?: string
  /** challenge_token from the most recent CreateChallenge (OTP factors). */
  challengeToken?: string
  /** Signed verification_attestation JWTs collected so far (the attestation container). */
  attestations: string[]
  /** Session access token issued by RedeemLoginIntent on success. */
  accessToken?: string
  /** Persistent journey id sent for correlation across the ceremony. */
  journeyId: string
  /** Masked email/phone the OTP was delivered to, for display. */
  maskedDeliveryTarget?: string
}

export const cashAuthInitialState: CashAuthState = {
  loginIntentToken: undefined,
  challengeToken: undefined,
  attestations: [],
  accessToken: undefined,
  journeyId: uuidv4(),
  maskedDeliveryTarget: undefined,
}

const cashAuthSlice = createSlice({
  name: 'cashAuth',
  initialState: cashAuthInitialState,
  reducers: {
    setLoginIntent(state, action: PayloadAction<{ loginIntentToken: string }>) {
      state.loginIntentToken = action.payload.loginIntentToken
      // A new login intent starts a fresh ceremony — clear any per-intent state left over
      // from a prior attempt (e.g. restarting inline auth after verifying a factor), so the
      // next redeem can't carry attestations/challenge from the previous login_intent_token.
      state.challengeToken = undefined
      state.attestations = []
      state.accessToken = undefined
      state.maskedDeliveryTarget = undefined
    },
    setChallenge(state, action: PayloadAction<{ challengeToken: string; maskedDeliveryTarget?: string }>) {
      state.challengeToken = action.payload.challengeToken
      state.maskedDeliveryTarget = action.payload.maskedDeliveryTarget
    },
    addAttestation(state, action: PayloadAction<string>) {
      if (!state.attestations.includes(action.payload)) {
        state.attestations.push(action.payload)
      }
    },
    setRedeemSuccess(state, action: PayloadAction<{ accessToken: string; attestations: string[] }>) {
      state.accessToken = action.payload.accessToken
      state.attestations = action.payload.attestations
    },
    resetCashAuth(state) {
      state.loginIntentToken = undefined
      state.challengeToken = undefined
      state.attestations = []
      state.accessToken = undefined
      state.maskedDeliveryTarget = undefined
      state.journeyId = uuidv4()
    },
  },
})

export const { setLoginIntent, setChallenge, addAttestation, setRedeemSuccess, resetCashAuth } = cashAuthSlice.actions

export default cashAuthSlice.reducer
